import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  TextArea,
  TextInput,
} from 'shared/components';
import { debouncedValidateJSON, validateJSON } from 'shared/validation';
import { patchLicenseUserGroup } from 'licenseUsers/actions';
import './EditLicenseUserGroupModal.scss';

const formatMetadataValue = (metadata) => {
  if (!metadata) {
    return '';
  }

  if (typeof metadata === 'string') {
    return metadata;
  }

  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return '';
  }
};

const EditLicenseUserGroupModal = ({ closeCb, confirmCb, group, groupID }) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);

  const [name, setName] = useState(get(group, 'name') || '');
  const [description, setDescription] = useState(get(group, 'description', ''));
  const [metadata, setMetadata] = useState(
    formatMetadataValue(get(group, 'metadata')),
  );
  const [metadataError, setMetadataError] = useState('');

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }

    return setDirtyFormDisplay(true);
  };

  const validateForm = async () => {
    const metadataErr = await validateJSON(metadata);
    setMetadataError(metadataErr);

    return !metadataErr;
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (isLoading) {
      return false;
    }

    if (!isDirty) {
      closeCb();
      return true;
    }

    const isValid = await validateForm();
    if (!isValid) {
      return false;
    }

    setLoading(true);

    let parsedMetadata = {};
    if (metadata) {
      try {
        parsedMetadata = JSON.parse(metadata);
      } catch {
        setLoading(false);
        setMetadataError(t('Metadata must be valid JSON object'));
        return false;
      }
    }

    const data = {
      name,
      description,
      metadata: parsedMetadata,
    };

    return patchLicenseUserGroup(groupID, data)
      .then(() => {
        setLoading(false);
        Notification('success', t('Changes saved successfully'));
        confirmCb();
        return true;
      })
      .catch(() => {
        setLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
        return false;
      });
  };

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={t('Edit user group')}
      size="md"
    >
      <form className="EditLicenseUserGroupModal" onSubmit={handleSubmit}>
        <div className="form-row">
          <Label text={t('Code')} inputId="license-user-group-code" />
          <TextInput
            id="license-user-group-code"
            value={get(group, 'code') || ''}
            disabled
          />
        </div>

        <div className="form-row">
          <Label text={t('Name')} inputId="license-user-group-name" />
          <TextInput
            id="license-user-group-name"
            value={name}
            handleChange={(val) => {
              setDirty(true);
              setName(val);
            }}
          />
        </div>

        <div className="form-row">
          <Label
            text={t('Description')}
            inputId="license-user-group-description"
          />
          <TextInput
            id="license-user-group-description"
            value={description}
            handleChange={(val) => {
              setDirty(true);
              setDescription(val);
            }}
          />
        </div>

        <div className="form-row">
          <Label
            text={t('Metadata JSON')}
            inputId="license-user-group-metadata"
          />
          <TextArea
            id="license-user-group-metadata"
            rows="4"
            value={metadata}
            error={metadataError}
            handleChange={(val) => {
              setDirty(true);
              setMetadata(val);
              debouncedValidateJSON(val).then((err) => setMetadataError(err));
            }}
          />
        </div>

        <button type="submit" style={{ display: 'none' }} />
      </form>

      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

EditLicenseUserGroupModal.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  group: PropTypes.object.isRequired,
  groupID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default EditLicenseUserGroupModal;
